// frontend/src/pages/CaptivePortal.jsx
// DWIFI Captive Portal — customer-facing, mobile-first, self-contained.
// Public route (no login), reached from the MikroTik walled garden once a
// router is live. Deliberately self-contained: no external CDN assets, no
// images, only inline Tailwind (already bundled) — a walled garden may only
// reach our own domain, so nothing here can depend on anything else. The
// default logo is the bundled inline-SVG DWILogoIcon for the same reason;
// an admin-set custom logo (CAPTIVE_PORTAL_LOGO_URL) must be same-origin or
// a data: URI to actually load behind the walled garden.
// Reuses the existing /api/captive-portal/* endpoints as-is; no money-core
// touched, no RADIUS enforcement here (that arrives with the real router —
// this flow completes to a success state and provisions the RADIUS record).
import { useState, useEffect, useCallback, useRef } from 'react';
import { captivePortalAPI, configAPI } from '../services/api';
import { DWILogoIcon } from '../components/DWILogo';

const NAVY = '#0D3468';
const BLUE = '#1B61AC';
const CYAN = '#00A8DB';

function fmtUGX(n) {
  return 'UGX ' + Math.round(n || 0).toLocaleString();
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function hoursRemaining(allowedUntil) {
  if (!allowedUntil) return 0;
  return (new Date(allowedUntil).getTime() - Date.now()) / 3600000;
}

function errMsg(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback || 'Something went wrong';
}

// Stage 2 (layout only): the landing splits the SAME package list the portal
// already loads into two buckets — a quick-buy card grid and a collapsible
// "Family Packages" section. Matched by IspPackage.category ('family' vs
// 'standard', admin-editable in the ISP admin tab's edit modal) — NOT a
// hardcoded packageId list, so any package an admin marks 'family' appears
// here automatically, no code deploy needed.

// Fallbacks for the admin-editable "New Installation" contact config keys
// (CAPTIVE_PORTAL_INSTALL_*). Kept in sync with the seed defaults in
// services/configService.js — the portal renders correctly on these alone
// even before the keys are seeded into the DB.
const DEFAULT_INSTALL_WHATSAPP = '0793730655';
const DEFAULT_INSTALL_EMAIL = 'admin@dwifintech.com';
const DEFAULT_INSTALL_TEXT = 'New to DWIFI? For home or family internet installation, get in touch and our team will set you up with a plan that fits.';

// Build a wa.me link from an admin-entered local number (e.g. "0793730655"
// or "+256 793 730655") — presentational only, no bearing on any payment.
function waLink(num) {
  let d = String(num || '').replace(/\D/g, '');
  if (d.startsWith('0')) d = '256' + d.slice(1);
  else if (!d.startsWith('256')) d = '256' + d;
  return 'https://wa.me/' + d;
}

// ── Shared bits ──────────────────────────────────────────────────────────────
function Header({ wifiName, logoUrl }) {
  return (
    <div
      style={{ background: `linear-gradient(180deg, ${NAVY} 0%, #0a2850 100%)` }}
      className="px-5 pt-9 pb-7 text-center relative overflow-hidden"
    >
      {/* Pure-CSS glow, no image asset — walled-garden safe */}
      <div
        aria-hidden="true"
        className="absolute -top-10 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full opacity-20 blur-2xl pointer-events-none"
        style={{ background: CYAN }}
      />
      <div className="relative inline-flex items-center justify-center mb-3">
        {logoUrl
          ? <img src={logoUrl} alt={wifiName || 'DWIFI'} className="w-14 h-14 rounded-2xl object-contain bg-white/10 p-1" />
          : <DWILogoIcon size={56} />}
      </div>
      <h1 className="relative text-white text-2xl font-black tracking-tight">{wifiName || 'DWIFI'}</h1>
    </div>
  );
}

function Footer({ brandText, onTermsClick }) {
  return (
    <div className="text-center py-4 px-5">
      <p className="text-[11px] text-gray-400">{brandText || 'Powered by DWI Fintech'}</p>
      {onTermsClick && (
        <button onClick={onTermsClick} className="text-[11px] text-gray-400 underline mt-1">
          Terms &amp; Conditions
        </button>
      )}
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled, loading, type = 'button' }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className="w-full py-3.5 rounded-xl font-bold text-white text-[15px] disabled:opacity-50 transition-all hover:brightness-110 active:scale-[0.99]"
      style={{ background: `linear-gradient(135deg, ${BLUE}, ${CYAN})` }}
    >
      {loading ? 'Please wait…' : children}
    </button>
  );
}

function SecondaryButton({ children, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full py-3.5 rounded-xl font-bold text-[15px] border-2 disabled:opacity-50 transition-all hover:bg-blue-50 active:scale-[0.99]"
      style={{ borderColor: BLUE, color: BLUE }}
    >
      {children}
    </button>
  );
}

function TextField({ label, value, onChange, type = 'text', placeholder, inputMode, maxLength, max, autoComplete, note }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        max={max}
        autoComplete={autoComplete}
        // The rest of this app's global body style is a dark theme
        // (near-white text on navy) — text-gray-900/bg-white/border here
        // are all set EXPLICITLY rather than left to inherit, so this
        // always-light-themed public page never ends up with faint
        // near-white-on-white input text regardless of the parent app's
        // theme. text-base (16px) avoids the iOS auto-zoom-on-focus that
        // kicks in below 16px.
        className="w-full px-4 py-3 rounded-xl border-2 border-gray-400 bg-white text-gray-900 placeholder-gray-400 text-base focus:outline-none focus:border-cyan-600 focus:ring-2"
        style={{ '--tw-ring-color': CYAN }}
      />
      {note && <p className="text-xs text-gray-500 mt-1">{note}</p>}
    </div>
  );
}

function ErrorBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-3 flex items-start justify-between gap-2">
      <span>{message}</span>
      {onDismiss && <button onClick={onDismiss} className="text-red-400 flex-shrink-0">✕</button>}
    </div>
  );
}

function PackageCard({ pkg, onSelect, actionLabel = 'Select' }) {
  return (
    <button
      onClick={() => onSelect(pkg)}
      className="w-full text-left bg-white border-2 rounded-2xl p-4 mb-3 flex items-center justify-between transition-all hover:shadow-md active:scale-[0.99]"
      style={{ borderColor: '#e5e7eb' }}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="font-bold text-gray-900 text-[15px] truncate">{pkg.name}</p>
          {pkg.badge && (
            <span
              className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full text-white flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${BLUE}, ${CYAN})` }}
            >
              {pkg.badge}
            </span>
          )}
        </div>
        <p className="text-gray-500 text-xs">{pkg.durationLabel}{pkg.speedInfo ? ` · ${pkg.speedInfo}` : ''}{pkg.maxDevices > 1 ? ` · ${pkg.maxDevices} devices` : ''}</p>
      </div>
      <div className="text-right flex-shrink-0 ml-3">
        <p className="font-black text-lg" style={{ color: NAVY }}>{fmtUGX(pkg.priceUGX)}</p>
        <span className="text-xs font-semibold" style={{ color: CYAN }}>{actionLabel} →</span>
      </div>
    </button>
  );
}

// Compact card for the landing's quick-buy grid — name + price only, no
// speed/badge/duration line. Tapping runs the exact same startBuy() flow as
// the full-width PackageCard.
function QuickPackageCard({ pkg, onSelect }) {
  return (
    <button
      onClick={() => onSelect(pkg)}
      className="text-left bg-white border-2 rounded-2xl px-3 py-3 flex flex-col transition-all hover:shadow-md active:scale-[0.98]"
      style={{ borderColor: '#e5e7eb' }}
    >
      <span className="font-bold text-gray-900 text-[15px] leading-tight truncate">{pkg.name}</span>
      <span className="font-black text-lg leading-tight mt-1" style={{ color: NAVY }}>{fmtUGX(pkg.priceUGX)}</span>
      <span className="text-[11px] font-semibold mt-1.5" style={{ color: CYAN }}>Buy →</span>
    </button>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
    >
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Self-contained collapsible section — no external UI lib, walled-garden safe.
// Keeps the landing page short while leaving every option one tap away.
function Collapsible({ title, hint, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-2 rounded-2xl mb-2" style={{ borderColor: '#e5e7eb' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="min-w-0">
          <span className="block font-bold text-gray-900 text-sm">{title}</span>
          {hint && <span className="block text-[11px] text-gray-400 mt-0.5">{hint}</span>}
        </span>
        <span className="flex-shrink-0" style={{ color: BLUE }}><ChevronIcon open={open} /></span>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

// Required "I agree to the Terms & Conditions" gate — shared by every screen
// that can end in payment/connection (payWallet, payMomo, voucher
// redemption). One shared checkbox per purchase attempt; the caller
// resets it back to false whenever a new attempt starts (see startBuy /
// startVoucherRedeem / backToPackages) so consent is given fresh each time,
// not carried over silently from a previous purchase.
function TermsConsent({ checked, onChange, onViewTerms }) {
  return (
    <div className="flex items-start gap-2.5 mb-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded border-2 border-gray-400 flex-shrink-0"
        style={{ accentColor: CYAN }}
      />
      <p className="text-xs text-gray-600 leading-snug">
        <span onClick={() => onChange(!checked)} className="cursor-pointer">I agree to the </span>
        <button type="button" onClick={onViewTerms} className="font-semibold underline" style={{ color: BLUE }}>
          Terms &amp; Conditions
        </button>
      </p>
    </div>
  );
}

// Step-2 payment-method choice — a plain, equal-weight option row (neither
// method is "primary"), distinct from PackageCard's price-forward layout so
// the choice screen reads as a fork in the road, not another package pick.
function PayMethodOption({ icon, title, subtitle, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white border-2 rounded-2xl p-4 flex items-center gap-3 transition-all hover:shadow-md active:scale-[0.99]"
      style={{ borderColor: '#e5e7eb' }}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${BLUE}, ${CYAN})` }}
      >
        <span role="img" aria-hidden="true">{icon}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-bold text-gray-900 text-[15px]">{title}</p>
        <p className="text-gray-500 text-xs">{subtitle}</p>
      </div>
      <span className="text-gray-300 text-lg flex-shrink-0">→</span>
    </button>
  );
}

// Copies text to the clipboard. Tries the modern Clipboard API first, but
// falls back to the old execCommand('copy') trick — some captive-portal
// mini-browsers (embedded webviews some phones use for the walled-garden
// pop-up) don't expose navigator.clipboard at all, or only allow it in a
// secure context, which a plain-HTTP router redirect page is not. Returns
// true/false rather than throwing, so callers can show their own message.
async function copyText(text) {
  if (!text) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the execCommand fallback below
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// Small "Copy" pill next to a displayed credential — flips to "Copied!" for
// a moment. `copiedField`/`onCopy` are lifted to the parent so "Copy Both"
// can share the exact same visual feedback state.
function CopyButton({ text, fieldKey, copiedField, onCopy, label = 'Copy' }) {
  const isCopied = copiedField === fieldKey;
  return (
    <button
      type="button"
      onClick={() => onCopy(fieldKey, text)}
      className="text-[11px] font-bold px-2 py-1 rounded-lg ml-2 flex-shrink-0"
      style={isCopied ? { background: '#ecfdf5', color: '#059669' } : { background: '#eff6ff', color: BLUE }}
    >
      {isCopied ? 'Copied!' : label}
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function CaptivePortal() {
  const [settings, setSettings] = useState({
    welcomeMessage: 'Welcome to DWIFI', wifiName: 'DWIFI', brandText: 'Powered by DWI Fintech',
    freePreviewEnabled: false, freePreviewDomains: [], logoUrl: '', termsText: '',
    installWhatsapp: DEFAULT_INSTALL_WHATSAPP, installEmail: DEFAULT_INSTALL_EMAIL, installText: DEFAULT_INSTALL_TEXT,
  });
  const [packages, setPackages] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const [screen, setScreen] = useState('landing');
  const [error, setError] = useState(null);

  const [phone, setPhone] = useState('');
  const [account, setAccount] = useState(null); // { name, hasWallet, balanceUGX, subscription }

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isRenewal, setIsRenewal] = useState(false);

  // 'account' (default) | 'renew' — set by the landing's "Renew your plan"
  // section so a successful login lands on the existing renewPick screen
  // instead of the account screen. Presentation routing only; the renew
  // purchase itself is the unchanged startBuy(pkg, true) flow.
  const [loginIntent, setLoginIntent] = useState('account');

  const [purchaseResult, setPurchaseResult] = useState(null);
  const pollRef = useRef(null);

  // ── Copy-credential feedback (success screen) — 'username' | 'password' |
  // 'both' | null. Pure UI state, no data of its own; copyText() above does
  // the actual clipboard work.
  const [copiedField, setCopiedField] = useState(null);
  const copiedTimerRef = useRef(null);
  useEffect(() => () => { if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current); }, []);

  const handleCopy = async (fieldKey, text) => {
    const ok = await copyText(text);
    if (!ok) { setError('Could not copy — please select and copy the text manually'); return; }
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    setCopiedField(fieldKey);
    copiedTimerRef.current = setTimeout(() => setCopiedField(null), 1500);
  };

  // ── MikroTik hotspot hand-off ─────────────────────────────────────────────
  // Present only when this page was reached via the router's own redirect
  // (its custom login.html — see services/routerConfigService.js): the
  // router appends its RADIUS "link-login-only" URL (the address a login
  // form must POST username/password to, to actually grant this device
  // network access) plus mac/ip/router identifiers. Absent entirely for a
  // direct visit to dwifintech.com/dwifi (e.g. testing, or a bookmark) —
  // every purchase flow below already works with no hand-off at all, same
  // as before this existed; the customer just sees the username/password to
  // enter manually on the router's own page in that case.
  const [hotspotHandoff, setHotspotHandoff] = useState(null); // { linkLogin, mac, ip, router } | null
  const [hotspotLoginStatus, setHotspotLoginStatus] = useState('idle'); // 'idle' | 'connecting' | 'done'

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const linkLogin = params.get('link_login');
    if (linkLogin) {
      setHotspotHandoff({
        linkLogin,
        mac: params.get('mac') || '',
        ip: params.get('ip') || '',
        router: params.get('router') || '',
      });
    }
  }, []);

  // Completes the router's OWN hotspot login (the step that actually flips
  // this device's MAC to "authorized" at the router) by submitting the just-
  // provisioned RADIUS credentials to link-login-only — exactly the same
  // POST a customer would otherwise type by hand into the router's default
  // page. Submitted into a hidden iframe (not a full-page navigation) so the
  // customer stays on this branded success screen instead of being bounced
  // to the router's own generic page. A same-network form POST like this is
  // not subject to CORS (unlike fetch/XHR), which is exactly why MikroTik's
  // own default login.html uses a plain form POST for this too.
  const completeHotspotLogin = useCallback((username, password) => {
    if (!hotspotHandoff?.linkLogin || !username || !password) return;
    setHotspotLoginStatus('connecting');
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = hotspotHandoff.linkLogin;
    form.target = 'dwifi-hotspot-login-frame';
    form.style.display = 'none';
    const fields = { username, password, dst: '', popup: 'true' };
    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }, [hotspotHandoff]);

  // captive_portal_connect_redirect (server sends connectMode 'redirect'):
  // the same login POST, but as a full-page navigation. A hidden-iframe
  // login does authorize the device at the router immediately (2026-10-21:
  // RADIUS Login OK the same second as payment) — but the phone's own
  // captive-portal check doesn't notice until its next background probe,
  // which can be minutes. A top-level navigation through the router makes
  // the phone re-check at once. dst brings the customer straight back to
  // this page's success screen (the router's stock alogin.html redirects
  // to it), restored from ?connected=<reference>.
  const redirectHotspotLogin = useCallback((username, password, reference) => {
    if (!hotspotHandoff?.linkLogin || !username || !password) return false;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = hotspotHandoff.linkLogin;
    form.style.display = 'none';
    const dst = `${window.location.origin}${window.location.pathname}?connected=${encodeURIComponent(reference)}`;
    const fields = { username, password, dst };
    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
    return true;
  }, [hotspotHandoff]);

  // Belt-and-suspenders alongside sw.js's own /dwifi + /api/* bypass (v33):
  // a device that already installed an OLDER, broken service worker on a
  // prior visit stays controlled by that old SW until it either updates in
  // the background or this page drops it — waiting on the former could mean
  // this exact customer hits the same "Something went wrong" again on this
  // very visit. Unregistering here is best-effort (per spec an ALREADY-
  // controlling SW may keep controlling this specific page load until
  // navigation/close — it is not guaranteed to un-intercept requests already
  // in flight this instant), but costs nothing and guarantees the NEXT load
  // of this page is never SW-controlled at all.
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations()
        .then((regs) => regs.forEach((r) => r.unregister()))
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, pkgRes] = await Promise.all([
          configAPI.getPublic(),
          captivePortalAPI.listPackages(),
        ]);
        const byKey = Object.fromEntries((cfgRes.configs || []).map((c) => [c.key, c.value]));
        setSettings({
          welcomeMessage: byKey.CAPTIVE_PORTAL_WELCOME_MESSAGE || 'Welcome to DWIFI',
          wifiName: byKey.CAPTIVE_PORTAL_WIFI_NAME || 'DWIFI',
          brandText: byKey.CAPTIVE_PORTAL_BRAND_TEXT || 'Powered by DWI Fintech',
          freePreviewEnabled: !!byKey.CAPTIVE_PORTAL_FREE_PREVIEW_ENABLED,
          freePreviewDomains: byKey.CAPTIVE_PORTAL_FREE_PREVIEW_DOMAINS || [],
          logoUrl: byKey.CAPTIVE_PORTAL_LOGO_URL || '',
          termsText: byKey.CAPTIVE_PORTAL_TERMS_TEXT || 'Terms & Conditions are not available right now — please contact support before proceeding.',
          installWhatsapp: byKey.CAPTIVE_PORTAL_INSTALL_WHATSAPP || DEFAULT_INSTALL_WHATSAPP,
          installEmail: byKey.CAPTIVE_PORTAL_INSTALL_EMAIL || DEFAULT_INSTALL_EMAIL,
          installText: byKey.CAPTIVE_PORTAL_INSTALL_TEXT || DEFAULT_INSTALL_TEXT,
        });
        setPackages(pkgRes.packages || []);
      } catch (err) {
        setError(errMsg(err, 'Could not load the hotspot portal — please refresh.'));
      } finally {
        setLoadingInitial(false);
      }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const goTo = (next) => { setError(null); setInsufficientBalance(false); setScreen(next); };

  // ── Terms & Conditions — reachable from anywhere (footer link) or from a
  // purchase screen's consent checkbox; always returns to where it was opened.
  const [termsReturnScreen, setTermsReturnScreen] = useState('landing');
  const openTerms = (returnTo) => { setTermsReturnScreen(returnTo || screen); goTo('terms'); };

  // ── Identify ────────────────────────────────────────────────────────────
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPin, setLoginPin] = useState('');
  const [identifyLoading, setIdentifyLoading] = useState(false);

  const doLogin = async () => {
    if (!loginPhone || !loginPin) { setError('Enter your phone number and PIN'); return; }
    setIdentifyLoading(true); setError(null);
    try {
      const res = await captivePortalAPI.login({ phone: loginPhone, credential: loginPin });
      setPhone(loginPhone);
      setAccount(res);
      setSignupEmail(null); // a login isn't a signup — don't show a stale signup-email banner from an earlier session
      if (loginIntent === 'payWallet') {
        // Detoured here from the payment-choice screen because there was no
        // identified session yet — now that login succeeded, continue on to
        // the wallet payment screen instead of the account screen.
        setBuyPhone(loginPhone);
        goTo('payWallet');
      } else {
        // Renew intent → the existing renewPick screen, but only when there's
        // actually a subscription to renew (preserves the original invariant
        // that renewPick is only ever reached with account.subscription set).
        goTo(loginIntent === 'renew' && res.subscription ? 'renewPick' : 'account');
      }
      setLoginIntent('account');
    } catch (err) { setError(errMsg(err, 'Login failed')); }
    finally { setIdentifyLoading(false); }
  };

  const [signupName, setSignupName] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupPin, setSignupPin] = useState('');
  // Shown once, right after signup — the only way the customer ever learns
  // the (synthetic) email half of their full DWIPay app credentials. Cleared
  // on dismiss or once they navigate away from the account screen.
  const [signupEmail, setSignupEmail] = useState(null);

  const doSignup = async () => {
    if (!signupName.trim() || !signupPhone || !signupPin) { setError('Fill in your name, phone, and a 6-digit PIN'); return; }
    if (!/^\d{6}$/.test(signupPin)) { setError('PIN must be exactly 6 digits'); return; }
    setIdentifyLoading(true); setError(null);
    try {
      const res = await captivePortalAPI.signup({ phone: signupPhone, name: signupName.trim(), pin: signupPin });
      setPhone(signupPhone);
      setAccount({ ...res, subscription: null });
      setSignupEmail(res.email || null);
      goTo('account');
    } catch (err) { setError(errMsg(err, 'Could not create your account')); }
    finally { setIdentifyLoading(false); }
  };

  // ── Buy ─────────────────────────────────────────────────────────────────
  const [termsAgreed, setTermsAgreed] = useState(false);

  const startBuy = (pkg, renewal = false) => {
    setSelectedPackage(pkg);
    setIsRenewal(renewal);
    setBuyPhone(phone || '');
    setTermsAgreed(false);
    goTo('payChoice');
  };

  // Step 2 of the payment-choice flow — routes to a dedicated screen for
  // ONLY the chosen method (payWallet or payMomo), never both at once.
  // Wallet requires an identified session (the `account` state, set by a
  // prior Login/Signup this visit) since the backend has no session of its
  // own to check — if there isn't one yet, detour through the existing
  // login screen first, then land on payWallet via loginIntent (see doLogin).
  const choosePayWallet = () => {
    if (account) { setBuyPhone(phone || ''); goTo('payWallet'); }
    else { setLoginIntent('payWallet'); goTo('login'); }
  };
  const choosePayMomo = () => goTo('payMomo');

  const [buyPhone, setBuyPhone] = useState('');
  const [walletPin, setWalletPin] = useState('');
  const [buyLoading, setBuyLoading] = useState(false);
  // Insufficient-balance is handled as its own guided state, not a plain
  // error — new customers start with an empty wallet, so this is the
  // expected first-purchase path, not a dead end. See guideToMobileMoney.
  const [insufficientBalance, setInsufficientBalance] = useState(false);

  const payWithWallet = async () => {
    if (!buyPhone || !walletPin) { setError('Enter your phone number and PIN'); return; }
    if (!termsAgreed) { setError('Please agree to the Terms & Conditions to continue'); return; }
    setBuyLoading(true); setError(null); setInsufficientBalance(false);
    try {
      const res = await captivePortalAPI.buyWithWallet({
        phone: buyPhone, credential: walletPin, packageId: selectedPackage._id, isRenewal, termsAgreed: true,
        router: hotspotHandoff?.router || undefined,
        mac: hotspotHandoff?.mac || undefined, ip: hotspotHandoff?.ip || undefined,
      });
      setPurchaseResult(res);
      setPhone(buyPhone);
      completeHotspotLogin(res.radiusUsername, res.radiusPassword);
      goTo('success');
    } catch (err) {
      if (err?.data?.code === 'INSUFFICIENT_BALANCE') setInsufficientBalance(true);
      else setError(errMsg(err, 'Payment failed'));
    }
    finally { setBuyLoading(false); setWalletPin(''); }
  };

  // Wallet and Mobile Money are now separate screens (payWallet / payMomo),
  // so "pay with Mobile Money instead" after an insufficient-balance result
  // is a screen switch, not a scroll — same guided recovery, cleaner nav.
  const guideToMobileMoney = () => {
    setInsufficientBalance(false);
    goTo('payMomo');
  };

  const [momoMethod, setMomoMethod] = useState('mtn_momo');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [pollReference, setPollReference] = useState(null);
  const [pollFailedMsg, setPollFailedMsg] = useState(null);
  // Set while the server is still confirming with Yo! (no answer in time),
  // or when the server handed back an earlier unresolved payment instead of
  // charging again — either way: wait, don't pay again.
  const [pollNotice, setPollNotice] = useState(null);
  const [connectedViaRedirect, setConnectedViaRedirect] = useState(false);

  // Back from the router after redirectHotspotLogin: show the success screen.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('connected');
    if (!ref) return;
    (async () => {
      try {
        const res = await captivePortalAPI.getStatus(ref);
        if (res.status === 'paid') {
          setPurchaseResult(res);
          setConnectedViaRedirect(true);
          setScreen('success');
        }
      } catch { /* fall back to the normal landing screen */ }
    })();
  }, []);

  const payWithMobileMoney = async () => {
    if (!buyPhone) { setError('Enter your phone number'); return; }
    if (!termsAgreed) { setError('Please agree to the Terms & Conditions to continue'); return; }
    setBuyLoading(true); setError(null); setInsufficientBalance(false);
    try {
      const res = await captivePortalAPI.buyWithMobileMoney({
        phone: buyPhone, packageId: selectedPackage._id, method: momoMethod,
        topUpAmount: topUpAmount ? Number(topUpAmount) : undefined, isRenewal, termsAgreed: true,
        router: hotspotHandoff?.router || undefined,
        mac: hotspotHandoff?.mac || undefined, ip: hotspotHandoff?.ip || undefined,
      });
      setPhone(buyPhone);
      setPollReference(res.reference);
      setPollNotice(res.resumed ? res.message : null);
      goTo('momoPay');
      startPolling(res.reference);
    } catch (err) { setError(errMsg(err, 'Could not start the mobile money payment')); }
    finally { setBuyLoading(false); }
  };

  const startPolling = (reference) => {
    setPollFailedMsg(null);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await captivePortalAPI.getStatus(reference);
        if (res.status === 'paid') {
          clearInterval(pollRef.current);
          setPurchaseResult(res);
          if (res.connectMode === 'redirect' && redirectHotspotLogin(res.radiusUsername, res.radiusPassword, res.reference)) return;
          completeHotspotLogin(res.radiusUsername, res.radiusPassword);
          goTo('success');
        } else if (res.status === 'pending' && res.confirming) {
          setPollNotice("The network is taking longer than usual to confirm your payment. Please don't pay again — this page will update automatically.");
        } else if (res.status === 'failed') {
          clearInterval(pollRef.current);
          setPollFailedMsg(res.error || 'Payment was not completed');
        }
      } catch {
        // transient poll error — keep trying, the interval will retry
      }
    // 9s, not 3s: every device at a hotspot venue shares one public IP, so a
    // fast loop from many concurrent payers piles up against the shared-IP
    // rate limit. 9s is well within a normal USSD-approval wait and cuts poll
    // volume ~3x. The status endpoint is also reference-keyed server-side now.
    }, 9000);
  };

  const retryMomo = () => { setPollFailedMsg(null); setPollNotice(null); goTo('payMomo'); };

  const backToPackages = () => {
    setSelectedPackage(null);
    setPurchaseResult(null);
    setWalletPin('');
    setVoucherCode('');
    setTermsAgreed(false);
    goTo(account ? 'account' : 'landing');
  };

  // ── Redeem Voucher — a third way to get online, alongside wallet and
  // mobile money. The code determines its own package, so this is a
  // top-level entry point (landing/account), not nested under the
  // payChoice/payWallet/payMomo flow.
  const [voucherPhone, setVoucherPhone] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherLoading, setVoucherLoading] = useState(false);

  // ── Manual RADIUS credential login — for a customer who already bought a
  // package (any method) and has the username/password from that purchase's
  // success screen, but got bounced back here (e.g. auto-login didn't run or
  // failed) and needs to reconnect this or another device. Pure frontend:
  // reuses completeHotspotLogin exactly as the post-purchase flows do — no
  // API call, no wallet/payment/RADIUS-provisioning code touched. Only
  // rendered when hotspotHandoff exists (same gate the post-purchase
  // "device granted access" banner already uses) since there is no
  // link-login-only target to submit to otherwise.
  const [radiusLoginCode, setRadiusLoginCode] = useState('');
  const [radiusLoginAttempted, setRadiusLoginAttempted] = useState(false);

  const connectWithRadiusCredential = () => {
    const code = radiusLoginCode.trim();
    if (!code) {
      setError('Enter your access code'); return;
    }
    setError(null);
    setRadiusLoginAttempted(true);
    completeHotspotLogin(code, code);
  };

  const startVoucherRedeem = () => {
    setVoucherPhone(phone || '');
    setVoucherCode('');
    setTermsAgreed(false);
    goTo('voucher');
  };

  const doRedeemVoucher = async () => {
    if (!voucherPhone || !voucherCode.trim()) { setError('Enter your phone number and voucher code'); return; }
    if (!termsAgreed) { setError('Please agree to the Terms & Conditions to continue'); return; }
    setVoucherLoading(true); setError(null);
    try {
      const res = await captivePortalAPI.redeemVoucher({
        phone: voucherPhone, code: voucherCode.trim(), termsAgreed: true,
        router: hotspotHandoff?.router || undefined,
        mac: hotspotHandoff?.mac || undefined, ip: hotspotHandoff?.ip || undefined,
      });
      setPurchaseResult(res);
      setPhone(voucherPhone);
      setIsRenewal(false);
      completeHotspotLogin(res.radiusUsername, res.radiusPassword);
      goTo('success');
    } catch (err) { setError(errMsg(err, 'Could not redeem this voucher code')); }
    finally { setVoucherLoading(false); }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  if (loadingInitial) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse text-gray-400 text-sm">Loading…</div>
      </div>
    );
  }

  // Same list the portal already loaded, split for the landing layout only.
  // Order within each bucket is preserved from the API's own sort.
  const familyPackages = packages.filter((p) => p.category === 'family');
  const quickPackages = packages.filter((p) => p.category !== 'family');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header wifiName={settings.wifiName} logoUrl={settings.logoUrl} />

      <div className="flex-1 max-w-md w-full mx-auto px-4 -mt-5">
        <div className="bg-white rounded-3xl shadow-xl shadow-black/10 p-5">

          {screen === 'landing' && (
            <>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 text-center">{settings.welcomeMessage}</p>
              {settings.freePreviewEnabled && settings.freePreviewDomains.length > 0 && (
                <div className="bg-blue-50 text-blue-700 text-xs rounded-xl px-3 py-2 mb-4">
                  Free preview available: {settings.freePreviewDomains.join(', ')}
                </div>
              )}
              <ErrorBanner message={error} onDismiss={() => setError(null)} />

              {/* Quick packages — tap to buy, existing purchase flow */}
              <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Buy Internet</h2>
              {quickPackages.length === 0 ? (
                <p className="text-gray-400 text-sm py-6 text-center">No packages available right now.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {quickPackages.map((pkg) => (
                    <QuickPackageCard key={pkg._id} pkg={pkg} onSelect={(p) => startBuy(p, false)} />
                  ))}
                </div>
              )}

              {/* Family packages — existing home/family clients, existing purchase flow */}
              {familyPackages.length > 0 && (
                <Collapsible title="Family Packages (existing clients)" hint="Home / family plans — buy or renew">
                  <p className="text-gray-500 text-xs mb-3">
                    For customers who already have DWIFI home &amp; family internet. New customer? See “New Installation” below.
                  </p>
                  {familyPackages.map((pkg) => (
                    <PackageCard key={pkg._id} pkg={pkg} onSelect={(p) => startBuy(p, false)} actionLabel="Buy" />
                  ))}
                </Collapsible>
              )}

              {/* New installation — admin-editable contact info, no packages.
                  Same-tab navigation (no target="_blank"): captive-portal
                  mini-browsers routinely block new tabs / app hand-off, so a
                  popup link would silently do nothing. The number is also
                  shown as plain selectable text below as a fallback for when
                  the link itself can't open (e.g. wa.me not yet reachable
                  from inside the walled garden). */}
              <Collapsible title="New Installation" hint="Home / family internet — talk to us">
                <p className="text-gray-600 text-xs leading-relaxed mb-3 whitespace-pre-line">{settings.installText}</p>
                <a
                  href={waLink(settings.installWhatsapp)}
                  rel="noopener noreferrer"
                  className="block w-full text-center py-3 rounded-xl font-bold text-white text-[15px] mb-3"
                  style={{ background: `linear-gradient(135deg, ${BLUE}, ${CYAN})` }}
                >
                  Chat on WhatsApp
                </a>
                <div className="text-xs text-gray-500 text-center space-y-1">
                  <p>
                    Or message us on WhatsApp:{' '}
                    <span className="font-semibold text-gray-800 select-all tracking-wide">{settings.installWhatsapp}</span>
                  </p>
                  <p>
                    Email:{' '}
                    <a href={`mailto:${settings.installEmail}`} className="font-semibold underline select-all" style={{ color: BLUE }}>
                      {settings.installEmail}
                    </a>
                  </p>
                </div>
              </Collapsible>

              {/* Voucher — existing redeem flow */}
              <Collapsible title="Have a voucher code?" hint="Redeem a code from an agent or shop">
                <p className="text-gray-500 text-xs mb-3">Enter a prepaid code to get online right away.</p>
                <PrimaryButton onClick={startVoucherRedeem}>Redeem a Voucher</PrimaryButton>
              </Collapsible>

              {/* Manual RADIUS credential login — only meaningful inside the
                  router's own walled-garden redirect (hotspotHandoff set);
                  hidden on a direct/bookmarked visit since there's nothing to
                  log this device into. */}
              {hotspotHandoff && (
                <Collapsible title="Already have an access code?" hint="Log in with a code from a previous purchase">
                  <p className="text-gray-500 text-xs mb-3">
                    Bought internet before and have an access code? Enter it here to connect this device.
                  </p>
                  <TextField
                    label="Access Code"
                    value={radiusLoginCode}
                    onChange={setRadiusLoginCode}
                    autoComplete="off"
                    placeholder="e.g. K7XPQ2"
                  />
                  <PrimaryButton onClick={connectWithRadiusCredential}>Log In to Connect</PrimaryButton>
                  {radiusLoginAttempted && hotspotLoginStatus !== 'idle' && (
                    // Same honesty fix as the purchase-success screen: iframe
                    // onLoad only means a response loaded, not that RADIUS
                    // accepted it — never assert a confirmed "granted access".
                    <div className="text-xs rounded-xl px-3 py-2 mt-3" style={{ background: '#eff6ff', color: BLUE }}>
                      {hotspotLoginStatus === 'done'
                        ? "Login attempted. If you're not online yet, double-check the access code above and try again."
                        : 'Connecting your device…'}
                    </div>
                  )}
                </Collapsible>
              )}

              {/* Renew — existing renew flow, reached via login */}
              <Collapsible title="Renew your plan" hint="Extend your current package">
                <p className="text-gray-500 text-xs mb-3">
                  Log in with your phone number and PIN to add more time to your active plan.
                </p>
                <PrimaryButton onClick={() => { setLoginIntent('renew'); goTo('login'); }}>Log In to Renew</PrimaryButton>
              </Collapsible>

              {/* Log in / create account — existing flows */}
              <div className="border-t border-gray-100 mt-3 pt-4">
                <div className="grid grid-cols-2 gap-2">
                  <SecondaryButton onClick={() => { setLoginIntent('account'); goTo('login'); }}>Log In</SecondaryButton>
                  <PrimaryButton onClick={() => goTo('signup')}>Create Account</PrimaryButton>
                </div>
                <p className="text-[11px] text-gray-400 text-center mt-2">Log in to see your plan, buy more, or renew.</p>
              </div>
            </>
          )}

          {screen === 'login' && (
            <>
              <button onClick={() => goTo(loginIntent === 'payWallet' ? 'payChoice' : 'landing')} className="text-sm text-gray-400 mb-3">← Back</button>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Log In</h2>
              <p className="text-gray-500 text-sm mb-4">
                {loginIntent === 'payWallet'
                  ? "Log in to pay with your DWIPay Wallet — enter your phone number and wallet PIN."
                  : "Enter your DWIPay phone number, then your wallet PIN if you've set one — otherwise your DWIPay account password."}
              </p>
              <ErrorBanner message={error} onDismiss={() => setError(null)} />
              <TextField label="Phone Number" value={loginPhone} onChange={setLoginPhone} inputMode="tel" autoComplete="tel" placeholder="07XXXXXXXX" />
              <TextField label="PIN or Password" value={loginPin} onChange={setLoginPin} type="password" placeholder="Wallet PIN or account password" />
              <div className="mt-2"><PrimaryButton onClick={doLogin} loading={identifyLoading}>Log In</PrimaryButton></div>
            </>
          )}

          {screen === 'signup' && (
            <>
              <button onClick={() => goTo('landing')} className="text-sm text-gray-400 mb-3">← Back</button>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Create Account</h2>
              <p className="text-gray-500 text-sm mb-4">Quick signup — creates your DWIPay wallet.</p>
              <ErrorBanner message={error} onDismiss={() => setError(null)} />
              <TextField label="Full Name" value={signupName} onChange={setSignupName} placeholder="Your name" />
              <TextField label="Phone Number" value={signupPhone} onChange={setSignupPhone} inputMode="tel" autoComplete="tel" placeholder="07XXXXXXXX" />
              <TextField
                label="Choose a 6-digit PIN"
                value={signupPin}
                onChange={setSignupPin}
                type="password"
                inputMode="numeric"
                maxLength={6}
                placeholder="••••••"
                note="This PIN is also your password for the full DWIPay app at dwifintech.com."
              />
              <div className="mt-2"><PrimaryButton onClick={doSignup} loading={identifyLoading}>Create Account</PrimaryButton></div>
            </>
          )}

          {screen === 'account' && account && (
            <>
              <p className="text-gray-500 text-xs mb-1">Welcome back,</p>
              <h2 className="text-lg font-bold text-gray-900 mb-4">{account.name}</h2>
              <ErrorBanner message={error} onDismiss={() => setError(null)} />

              {signupEmail && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4 text-xs text-blue-800">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-bold mb-1">Save this — it also logs you into the full DWIPay app</p>
                    <button onClick={() => setSignupEmail(null)} className="text-blue-400 flex-shrink-0">✕</button>
                  </div>
                  <p>Email: <span className="font-mono font-semibold">{signupEmail}</span></p>
                  <p>Password: the same 6-digit PIN you just chose</p>
                  <p className="mt-1 text-blue-600">Use these at dwifintech.com to access your wallet, investments, and more.</p>
                </div>
              )}

              {account.subscription ? (
                <div className="rounded-2xl p-4 mb-5" style={{ background: account.subscription.active ? '#ecfdf5' : '#fef2f2' }}>
                  <p className="font-bold text-sm mb-1" style={{ color: account.subscription.active ? '#059669' : '#dc2626' }}>
                    {account.subscription.active ? 'Active' : 'Expired'} — {account.subscription.packageName}
                  </p>
                  <p className="text-gray-600 text-xs mb-3">
                    {account.subscription.active ? 'Valid until' : 'Expired'} {fmtDate(account.subscription.allowedUntil)}
                  </p>
                  {(!account.subscription.active || hoursRemaining(account.subscription.allowedUntil) <= 24) && (
                    <PrimaryButton onClick={() => setScreen('renewPick')}>Renew Now</PrimaryButton>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm mb-5">You don't have an active plan yet — pick one below.</p>
              )}

              <button
                onClick={startVoucherRedeem}
                className="w-full text-center text-sm font-semibold py-2 mb-5 rounded-xl border border-dashed"
                style={{ color: BLUE, borderColor: '#c7d2e0' }}
              >
                Have a voucher code? Redeem it →
              </button>

              <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                {account.subscription?.active ? 'Buy Another Package' : 'Choose a Package'}
              </h2>
              {packages.map((pkg) => <PackageCard key={pkg._id} pkg={pkg} onSelect={(p) => startBuy(p, false)} actionLabel="Buy" />)}
            </>
          )}

          {screen === 'renewPick' && account && (
            <>
              <button onClick={() => goTo('account')} className="text-sm text-gray-400 mb-3">← Back</button>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Renew Your Plan</h2>
              <p className="text-gray-500 text-sm mb-4">Time gets added on top of what you already have.</p>
              {packages.map((pkg) => <PackageCard key={pkg._id} pkg={pkg} onSelect={(p) => startBuy(p, true)} actionLabel="Renew" />)}
            </>
          )}

          {screen === 'voucher' && (
            <>
              <button onClick={() => goTo(account ? 'account' : 'landing')} className="text-sm text-gray-400 mb-3">← Back</button>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Redeem a Voucher</h2>
              <p className="text-gray-500 text-sm mb-4">Bought a code from an agent or shop? Enter it here to get online right away.</p>
              <ErrorBanner message={error} onDismiss={() => setError(null)} />
              <TextField label="Phone Number" value={voucherPhone} onChange={setVoucherPhone} inputMode="tel" autoComplete="tel" placeholder="07XXXXXXXX" />
              <TextField
                label="Voucher Code"
                value={voucherCode}
                onChange={(v) => setVoucherCode(v.toUpperCase())}
                autoComplete="off"
                placeholder="e.g. ABCD1234"
                note="Codes aren't case-sensitive."
              />
              <TermsConsent checked={termsAgreed} onChange={setTermsAgreed} onViewTerms={() => openTerms('voucher')} />
              <div className="mt-2"><PrimaryButton onClick={doRedeemVoucher} loading={voucherLoading} disabled={!termsAgreed}>Redeem Voucher</PrimaryButton></div>
            </>
          )}

          {/* STEP 2 — focused choice, nothing else on the screen but the
              package summary and the two payment options. */}
          {screen === 'payChoice' && selectedPackage && (
            <>
              <button onClick={backToPackages} className="text-sm text-gray-400 mb-3">← Back</button>
              {isRenewal && <div className="bg-blue-50 text-blue-700 text-xs rounded-xl px-3 py-2 mb-3 font-semibold">Renewing your access</div>}
              <div className="rounded-2xl p-4 mb-5" style={{ background: NAVY }}>
                <p className="text-white/70 text-xs">{selectedPackage.durationLabel}</p>
                <p className="text-white font-black text-xl">{selectedPackage.name}</p>
                <p className="font-bold text-lg" style={{ color: CYAN }}>{fmtUGX(selectedPackage.priceUGX)}</p>
              </div>
              <ErrorBanner message={error} onDismiss={() => setError(null)} />
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Choose how to pay</p>
              <div className="space-y-3">
                <PayMethodOption
                  icon="👛"
                  title="Pay with DWIPay Wallet"
                  subtitle="Instant — uses your DWIPay wallet balance"
                  onClick={choosePayWallet}
                />
                <PayMethodOption
                  icon="📱"
                  title="Pay with Mobile Money"
                  subtitle="MTN MoMo or Airtel Money"
                  onClick={choosePayMomo}
                />
              </div>
            </>
          )}

          {/* STEP 3a — Wallet ONLY. Reached with an identified session
              already (choosePayWallet routes through /login first otherwise,
              see doLogin's loginIntent==='payWallet' branch). */}
          {screen === 'payWallet' && selectedPackage && (
            <>
              <button onClick={() => goTo('payChoice')} className="text-sm text-gray-400 mb-3">← Change payment method</button>
              <div className="rounded-2xl p-4 mb-4" style={{ background: NAVY }}>
                <p className="text-white/70 text-xs">{selectedPackage.durationLabel}</p>
                <p className="text-white font-black text-xl">{selectedPackage.name}</p>
                <p className="font-bold text-lg" style={{ color: CYAN }}>{fmtUGX(selectedPackage.priceUGX)}</p>
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Pay with DWIPay Wallet</h2>
              {insufficientBalance ? (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl px-4 py-3 mb-3">
                  <p className="text-amber-800 text-sm font-bold mb-1">Not enough wallet balance for this package.</p>
                  <p className="text-amber-700 text-xs mb-3">
                    New here? Most first-time customers pay by Mobile Money — it also tops up your wallet with any
                    extra you send, so next time you can pay straight from your wallet.
                  </p>
                  <button
                    onClick={guideToMobileMoney}
                    className="w-full py-2.5 rounded-xl text-sm font-bold text-white"
                    style={{ background: `linear-gradient(135deg, ${BLUE}, ${CYAN})` }}
                  >
                    Pay with Mobile Money Instead →
                  </button>
                </div>
              ) : (
                <ErrorBanner message={error} onDismiss={() => setError(null)} />
              )}
              <TextField label="Phone Number" value={buyPhone} onChange={setBuyPhone} inputMode="tel" autoComplete="tel" placeholder="07XXXXXXXX" />
              <TextField label="PIN or Password" value={walletPin} onChange={setWalletPin} type="password" placeholder="Wallet PIN or account password" />
              <TermsConsent checked={termsAgreed} onChange={setTermsAgreed} onViewTerms={() => openTerms('payWallet')} />
              <PrimaryButton onClick={payWithWallet} loading={buyLoading} disabled={!termsAgreed}>Pay {fmtUGX(selectedPackage.priceUGX)} from Wallet</PrimaryButton>
            </>
          )}

          {/* STEP 3b — Mobile Money ONLY. */}
          {screen === 'payMomo' && selectedPackage && (
            <>
              <button onClick={() => goTo('payChoice')} className="text-sm text-gray-400 mb-3">← Change payment method</button>
              <div className="rounded-2xl p-4 mb-4" style={{ background: NAVY }}>
                <p className="text-white/70 text-xs">{selectedPackage.durationLabel}</p>
                <p className="text-white font-black text-xl">{selectedPackage.name}</p>
                <p className="font-bold text-lg" style={{ color: CYAN }}>{fmtUGX(selectedPackage.priceUGX)}</p>
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Pay with Mobile Money</h2>
              <ErrorBanner message={error} onDismiss={() => setError(null)} />
              <TextField label="Phone Number" value={buyPhone} onChange={setBuyPhone} inputMode="tel" autoComplete="tel" placeholder="07XXXXXXXX" />
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  onClick={() => setMomoMethod('mtn_momo')}
                  className="py-2.5 rounded-xl text-sm font-bold border-2"
                  style={{ borderColor: momoMethod === 'mtn_momo' ? BLUE : '#e5e7eb', color: momoMethod === 'mtn_momo' ? BLUE : '#9ca3af' }}
                >MTN MoMo</button>
                <button
                  onClick={() => setMomoMethod('airtel_money')}
                  className="py-2.5 rounded-xl text-sm font-bold border-2"
                  style={{ borderColor: momoMethod === 'airtel_money' ? BLUE : '#e5e7eb', color: momoMethod === 'airtel_money' ? BLUE : '#9ca3af' }}
                >Airtel Money</button>
              </div>
              <TextField
                label="Top up extra (optional, UGX)"
                value={topUpAmount}
                onChange={setTopUpAmount}
                type="number"
                inputMode="numeric"
                placeholder="0"
                max={100000}
                autoComplete="off"
                note={`Total to pay: ${fmtUGX(selectedPackage.priceUGX + (Number(topUpAmount) || 0))}`}
              />
              <TermsConsent checked={termsAgreed} onChange={setTermsAgreed} onViewTerms={() => openTerms('payMomo')} />
              <PrimaryButton onClick={payWithMobileMoney} loading={buyLoading} disabled={!termsAgreed}>
                Pay with {momoMethod === 'mtn_momo' ? 'MTN MoMo' : 'Airtel Money'}
              </PrimaryButton>
            </>
          )}

          {screen === 'momoPay' && (
            <div className="text-center py-8">
              {!pollFailedMsg ? (
                <>
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: CYAN, borderTopColor: 'transparent' }} />
                  <p className="font-bold text-gray-900 mb-1">{pollNotice ? 'Confirming your payment…' : 'Approve the prompt on your phone…'}</p>
                  <p className="text-gray-500 text-sm">{pollNotice || 'Check your phone for a mobile money PIN prompt to complete payment.'}</p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center text-red-500 text-2xl">✕</div>
                  <p className="font-bold text-gray-900 mb-1">Payment not completed</p>
                  <p className="text-gray-500 text-sm mb-5">{pollFailedMsg}</p>
                  <PrimaryButton onClick={retryMomo}>Try Again</PrimaryButton>
                </>
              )}
            </div>
          )}

          {screen === 'success' && purchaseResult && (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-3xl text-white" style={{ background: '#22c55e' }}>✓</div>
              <h2 className="font-black text-xl text-gray-900 mb-1">✅ Payment Successful!</h2>
              <p className="text-gray-900 text-sm font-semibold mb-5">
                {connectedViaRedirect
                  ? "You're connected. If the internet doesn't work, enter this code on the WiFi login:"
                  : <>{isRenewal ? 'Your access has been extended. ' : ''}To get online, enter this code on the WiFi login:</>}
              </p>
              <ErrorBanner message={error} onDismiss={() => setError(null)} />

              {/* A background auto-login attempt still runs on purchase (see
                  completeHotspotLogin / the hidden iframe at the bottom of
                  this component) — left in place since it's harmless and may
                  start working once the RADIUS-side gap is fixed. But its
                  only signal (iframe onLoad) can't actually confirm success,
                  so nothing about it is shown here — the credential below is
                  always the real, honest way to connect, not a fallback for
                  when something invisible "didn't work". */}

              <div className="bg-gray-50 rounded-2xl p-4 text-left mb-3 space-y-2">
                {purchaseResult.radiusUsername && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500 flex-shrink-0">Access Code</span>
                    <span className="flex items-center min-w-0 justify-end">
                      <span className="font-mono font-semibold text-gray-900 tracking-wider truncate">{purchaseResult.radiusUsername}</span>
                      <CopyButton text={purchaseResult.radiusUsername} fieldKey="code" copiedField={copiedField} onCopy={handleCopy} />
                    </span>
                  </div>
                )}
                {purchaseResult.allowedUntil && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Valid Until</span>
                    <span className="font-semibold text-gray-900">{fmtDate(purchaseResult.allowedUntil)}</span>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500 mb-5">
                Connect to the WiFi, then enter this code as both the username and password on the login page to get online.
              </p>

              <PrimaryButton onClick={backToPackages}>Done</PrimaryButton>
            </div>
          )}

          {screen === 'terms' && (
            <>
              <button onClick={() => goTo(termsReturnScreen)} className="text-sm text-gray-400 mb-3">← Back</button>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Terms &amp; Conditions</h2>
              <p className="text-gray-500 text-xs mb-4">Please read before continuing.</p>
              <div className="whitespace-pre-line text-sm text-gray-700 leading-relaxed max-h-[55vh] overflow-y-auto border border-gray-200 rounded-xl p-4 bg-gray-50 mb-2">
                {settings.termsText}
              </div>
            </>
          )}
        </div>
      </div>

      <Footer brandText={settings.brandText} onTermsClick={() => openTerms(screen)} />

      {hotspotHandoff && (
        <iframe
          name="dwifi-hotspot-login-frame"
          title="hotspot-login"
          style={{ display: 'none' }}
          onLoad={() => setHotspotLoginStatus((s) => (s === 'connecting' ? 'done' : s))}
        />
      )}
    </div>
  );
}
